import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { Calculator, Flame } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  CALORIMETRY_CASES,
  WATER_SPECIFIC_HEAT_J_PER_G_K,
  calculateCalorimetry,
  checkEnthalpyAnswer,
  checkHeatAnswer,
  formatDecimalSigned,
  formatSig,
  formatSigned,
  type AnswerCheck,
  type CalorimetryCase,
} from './energyCalorimetry';
import {
  PROFILE_REACTIONS,
  activationEnergyRange,
  buildEnergyProfile,
  checkCatalystPrediction,
  type CatalystPrediction,
  type ProfileReaction,
} from './energyCalorimetrySenior';
import { CalorimetryExplore } from './EnergyCalorimetryExplore';
import { FuelsPanel, HessPanel } from './EnergyCalorimetryHessFuels';
import { SpontaneityPanel } from './EnergyCalorimetrySpontaneity';
import { EnergyProfileChart, FeedbackCard } from './EnergyCalorimetryVisuals';
import { ModelInputPanel } from './ModelInputs';

type SeniorTab = 'calorimetry' | 'profile' | 'hess' | 'fuels' | 'spontaneity';

const SENIOR_TABS: { id: SeniorTab; label: string }[] = [
  { id: 'calorimetry', label: 'Calorimetry' },
  { id: 'profile', label: 'Energy profile' },
  { id: 'hess', label: 'Hess’s law' },
  { id: 'fuels', label: 'Fuels' },
  { id: 'spontaneity', label: 'Spontaneity' },
];

type CalorimetryView = 'explore' | 'practice';

const CALORIMETRY_VIEWS: { id: CalorimetryView; label: string }[] = [
  { id: 'explore', label: 'Explore' },
  { id: 'practice', label: 'Practice' },
];

/** Explore (own readings, working always visible) and Practice (fixed cases, answer first). */
function CalorimetryPanel() {
  const [view, setView] = useState<CalorimetryView>('explore');
  return (
    <div className="space-y-4">
      <div className="inline-grid grid-cols-2 gap-1 rounded-full bg-surface p-1" aria-label="Calorimetry view">
        {CALORIMETRY_VIEWS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-pressed={view === entry.id}
            onClick={() => setView(entry.id)}
            className={`rounded-full px-4 py-2 text-sm font-extrabold ${view === entry.id ? 'bg-ink text-white' : 'text-slate2'}`}
          >
            {entry.label}
          </button>
        ))}
      </div>
      {view === 'explore' ? <CalorimetryExplore /> : <CalorimetryPractice />}
    </div>
  );
}

function CalorimetryPractice() {
  const [selected, setSelected] = useState<CalorimetryCase>(CALORIMETRY_CASES[0]);
  const [heatInput, setHeatInput] = useState('');
  const [enthalpyInput, setEnthalpyInput] = useState('');
  const [checked, setChecked] = useState<{ heat: AnswerCheck; enthalpy: AnswerCheck } | null>(
    null,
  );
  useScienceTutorSection('CalorimetryPanel', { selected: tutorSelection(selected), heatInput, enthalpyInput, checked });
  const result = useMemo(() => calculateCalorimetry(selected), [selected]);

  const choose = (entry: CalorimetryCase) => {
    setSelected(entry);
    setHeatInput('');
    setEnthalpyInput('');
    setChecked(null);
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
      <fieldset className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft">
        <legend className="sr-only">Choose an experiment</legend>
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          Experiments
        </p>
        <div className="mt-3 grid gap-2">
          {CALORIMETRY_CASES.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={selected.id === entry.id}
              onClick={() => choose(entry)}
              className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold ${selected.id === entry.id ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
            >
              {entry.name}
            </button>
          ))}
        </div>
      </fieldset>

      <section className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          {selected.kind}
        </p>
        <h3 className="mt-1 text-xl font-extrabold text-ink">{selected.name}</h3>
        <p className="mt-2 break-words font-mono text-sm font-bold text-ink">{selected.equation}</p>
        <p className="mt-2 text-sm font-medium leading-6 text-slate2">{selected.apparatus}</p>

        <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          <div className="rounded-2xl bg-surface p-3">
            <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">
              Mass of liquid, m
            </dt>
            <dd className="mt-1 font-extrabold text-ink">
              {selected.liquidVolumeMl} mL × 1.00 g mL⁻¹ = {formatSig(result.massG, 3)} g
            </dd>
          </div>
          <div className="rounded-2xl bg-surface p-3">
            <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">
              Temperature change, ΔT
            </dt>
            <dd className="mt-1 font-extrabold text-ink">
              {formatDecimalSigned(selected.temperatureChangeK, 1)} K
            </dd>
          </div>
          <div className="rounded-2xl bg-surface p-3">
            <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">
              Specific heat capacity, c
            </dt>
            <dd className="mt-1 font-extrabold text-ink">
              {WATER_SPECIFIC_HEAT_J_PER_G_K} J g⁻¹ K⁻¹ (water)
            </dd>
          </div>
          <div className="rounded-2xl bg-surface p-3">
            <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">
              Amount reacted, n
            </dt>
            <dd className="mt-1 font-extrabold text-ink">
              {selected.amountWorking} = {formatSig(selected.amountMol, 3)} mol
            </dd>
          </div>
        </dl>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-extrabold text-ink">
            q absorbed by the liquid (kJ)
            <input
              aria-label="Heat q in kJ"
              inputMode="decimal"
              value={heatInput}
              onChange={(event) => {
                setHeatInput(event.target.value);
                setChecked(null);
              }}
              className="mt-2 w-full rounded-2xl border border-hairline bg-white px-4 py-3 font-medium text-ink outline-none focus:border-brand-sky"
              placeholder="q = m c ΔT"
            />
          </label>
          <label className="text-sm font-extrabold text-ink">
            ΔH (kJ mol⁻¹)
            <input
              aria-label="Enthalpy change in kJ per mol"
              inputMode="decimal"
              value={enthalpyInput}
              onChange={(event) => {
                setEnthalpyInput(event.target.value);
                setChecked(null);
              }}
              className="mt-2 w-full rounded-2xl border border-hairline bg-white px-4 py-3 font-medium text-ink outline-none focus:border-brand-sky"
              placeholder="ΔH = −q / n"
            />
          </label>
        </div>
        <button
          type="button"
          onClick={() =>
            setChecked({
              heat: checkHeatAnswer(selected, heatInput),
              enthalpy: checkEnthalpyAnswer(selected, enthalpyInput),
            })
          }
          className="btn-pill-primary mt-5"
        >
          <Calculator className="mr-2 inline h-4 w-4" aria-hidden="true" />
          Check calculation
        </button>

        {checked ? (
          <div className="mt-5 grid gap-3" aria-label="Calorimetry feedback">
            <FeedbackCard title="q" check={checked.heat} />
            <FeedbackCard title="ΔH" check={checked.enthalpy} />
            <div className="rounded-2xl border border-hairline bg-surface p-4">
              <p className="text-sm font-extrabold text-ink">Compare with the reference value</p>
              <p className="mt-1 text-sm font-medium leading-6 text-slate2">
                Experiment: {formatSigned(result.deltaHkJPerMol, 3)} kJ mol⁻¹ · Reference:{' '}
                {formatSigned(selected.referenceDeltaHkJ, 3)} kJ mol⁻¹ per mol of{' '}
                {selected.perMolOf} · {formatSig(result.percentOfReference, 2)}% of the
                reference size
              </p>
              <div className="mt-3 h-3 overflow-hidden rounded-full bg-white" aria-hidden="true">
                <div
                  className="h-full rounded-full bg-brand-sunshine"
                  style={{ width: `${Math.min(100, result.percentOfReference)}%` }}
                />
              </div>
              <p className="mt-3 text-xs font-bold leading-5 text-slate2">
                <strong className="text-ink">Why the gap?</strong> {selected.heatLossNote}
              </p>
            </div>
          </div>
        ) : null}
        <p className="mt-4 text-xs font-medium leading-5 text-slate2">
          Sign convention: q is the energy gained by the water or solution. The reacting system
          loses that energy, so ΔH = −q / n. Readings are illustrative teaching values, not
          measurements.
        </p>
      </section>
    </div>
  );
}

const CATALYST_OPTIONS: { id: CatalystPrediction; label: string }[] = [
  { id: 'lower', label: 'ΔH becomes smaller' },
  { id: 'same', label: 'ΔH stays the same' },
  { id: 'higher', label: 'ΔH becomes larger' },
];

function ProfilePanel() {
  const [reaction, setReaction] = useState<ProfileReaction>(PROFILE_REACTIONS[0]);
  const range = useMemo(() => activationEnergyRange(reaction.deltaHkJ), [reaction]);
  const [ea, setEa] = useState(range.initial);
  const [catalyst, setCatalyst] = useState(false);
  const [prediction, setPrediction] = useState<CatalystPrediction | null>(null);
  const [checked, setChecked] = useState(false);
  useScienceTutorSection('ProfilePanel', { reaction: tutorSelection(reaction), ea, catalyst, prediction, checked });
  const profile = buildEnergyProfile(reaction.deltaHkJ, ea);

  const choose = (entry: ProfileReaction) => {
    setReaction(entry);
    setEa(activationEnergyRange(entry.deltaHkJ).initial);
    setCatalyst(false);
    setPrediction(null);
    setChecked(false);
  };

  return (
    <section className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
      <div className="flex flex-wrap gap-2" aria-label="Choose a reaction">
        {PROFILE_REACTIONS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-pressed={reaction.id === entry.id}
            onClick={() => choose(entry)}
            className={`rounded-full border px-4 py-2 text-sm font-extrabold ${reaction.id === entry.id ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
          >
            {entry.name}
          </button>
        ))}
      </div>
      <p className="mt-4 break-words font-mono text-sm font-bold text-ink">
        {reaction.equation} &nbsp; ΔH = {formatSigned(reaction.deltaHkJ, 3)} kJ
      </p>
      <div className="mt-4 grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="rounded-2xl bg-surface p-3">
          {profile ? (
            <EnergyProfileChart profile={profile} showCatalyst={catalyst} />
          ) : (
            <p role="alert" className="p-4 text-sm font-bold text-ink">
              This Ea cannot reach the products. Choose a larger activation energy.
            </p>
          )}
        </div>
        <div className="space-y-4">
          <ModelInputPanel
            key={reaction.id}
            legend="Illustrative activation energy"
            fields={[{ key: 'ea', label: 'Illustrative Ea', unit: 'kJ', min: range.min, max: range.max, step: range.step }]}
            values={{ ea }}
            onChange={(next) => setEa(next.ea)}
            sliderKey="ea"
          />
          <button
            type="button"
            aria-pressed={catalyst}
            onClick={() => setCatalyst((value) => !value)}
            className={`flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left text-sm font-extrabold ${catalyst ? 'border-brand-mint bg-wash-mint text-ink' : 'border-hairline bg-surface text-ink'}`}
          >
            Catalyst pathway <span>{catalyst ? 'On' : 'Off'}</span>
          </button>
          {profile ? (
            <p className="text-xs font-bold leading-5 text-slate2">
              Reverse Ea = Ea − ΔH = {formatSig(profile.reverseEa, 3)} kJ
              {catalyst ? ` · catalysed Ea ≈ ${formatSig(profile.catalysedEa, 3)} kJ` : ''}
            </p>
          ) : null}
          <fieldset>
            <legend className="text-sm font-extrabold text-ink">
              Predict: what does a catalyst do to ΔH?
            </legend>
            <div className="mt-2 grid gap-2">
              {CATALYST_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={prediction === option.id}
                  onClick={() => {
                    setPrediction(option.id);
                    setChecked(false);
                  }}
                  className={`rounded-2xl border px-4 py-2 text-left text-sm font-extrabold ${prediction === option.id ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>
          <button
            type="button"
            disabled={!prediction}
            onClick={() => {
              setChecked(true);
              setCatalyst(true);
            }}
            className="btn-pill-primary disabled:cursor-not-allowed disabled:opacity-40"
          >
            Add the catalyst and check
          </button>
          {checked && prediction ? (
            <FeedbackCard title="Catalyst" check={checkCatalystPrediction(prediction)} />
          ) : null}
        </div>
      </div>
      <p className="mt-4 text-xs font-medium leading-5 text-slate2">
        ΔH comes from reviewed standard data (25 °C, 100 kPa). Ea is illustrative: you choose it,
        and the catalysed pathway is drawn at about 60% of it. Real activation energies must come
        from kinetic measurements.
      </p>
    </section>
  );
}

export function SeniorEnergyMode() {
  const [tab, setTab] = useState<SeniorTab>('calorimetry');
  useScienceTutorSection('SeniorEnergyMode', { tab });
  return (
    <div className="space-y-5">
      <div
        className="grid grid-cols-2 gap-1 rounded-[1.5rem] bg-surface p-1 sm:grid-cols-3 lg:grid-cols-5"
        aria-label="Senior activity"
      >
        {SENIOR_TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-pressed={tab === entry.id}
            onClick={() => setTab(entry.id)}
            className={`rounded-full px-3 py-2 text-sm font-extrabold ${tab === entry.id ? 'bg-ink text-white' : 'text-slate2'}`}
          >
            {entry.id === 'fuels' ? (
              <Flame className="mr-1 inline h-4 w-4" aria-hidden="true" />
            ) : null}
            {entry.label}
          </button>
        ))}
      </div>
      {tab === 'calorimetry' ? <CalorimetryPanel /> : null}
      {tab === 'profile' ? <ProfilePanel /> : null}
      {tab === 'hess' ? <HessPanel /> : null}
      {tab === 'fuels' ? <FuelsPanel /> : null}
      {tab === 'spontaneity' ? <SpontaneityPanel /> : null}
    </div>
  );
}
